import { BroadcastIcon, GaugeIcon, TruckTrailerIcon, XIcon } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from 'components/alert/Alert';
import { Button } from 'components/button/Button';
import { LinkButton } from 'components/button/ButtonLink';
import { Card } from 'components/Card';
import { EmptyState } from 'components/empty-state/EmptyState';
import { ErrorState } from 'components/error-state/ErrorState';
import { Input } from 'components/input/Input';
import { TrailerMap, type TrailerMapMarker } from 'components/map/TrailerMap';
import { FilterField, FilterPanel } from 'components/page-templates/ListPage';
import { PageHeader } from 'components/PageHeader';
import { Pill } from 'components/pill/Pill';
import { SimpleSelect } from 'components/select/SimpleSelect';
import { DelayedSpinner } from 'components/Spinner';
import { StatCard } from 'components/stat-card/StatCard';
import { Table, type ITableHeader } from 'components/table/Table';
import { useTranslation } from 'contexts/translation-context';
import { LIVE_TRACKING_REFRESH_MS, useTelemetryConnections } from 'src/app/telemetry/models';
import { statusPill } from 'src/app/telemetry/status';
import { formatTimeSince } from '@utils/date';
import { TrailersHeading } from '../components/TrailersHeading';
import { useTrailerTrackingList, type TelemetryTrailerListItem } from '../models';

/**
 * The tracker states the filter offers.
 *
 * Only `tracked` has a flag on the endpoint; the other two ask about a joined timestamp (`lastSeenAt`)
 * that the query has no parameter for, so they narrow the fetched fleet in memory. That is safe because
 * the endpoint is deliberately unpaginated — the whole fleet is in this payload either way.
 */
type TrackerFilter = 'all' | 'tracked' | 'untracked' | 'silent';

/** A signal older than this is the number a planner acts on: the battery, not the outage's cause. */
const SILENT_AFTER_MS = 24 * 60 * 60 * 1000;

function isTrackerFilter(value: string | undefined): value is TrackerFilter {
  return value === 'all' || value === 'tracked' || value === 'untracked' || value === 'silent';
}

/** Tracked, but nothing heard for a day. Un-tracked trailers are not silent — they were never speaking. */
function isSilent(trailer: TelemetryTrailerListItem, now: number): boolean {
  if (!trailer.tracker) {
    return false;
  }
  return now - new Date(trailer.tracker.lastSeenAt).getTime() > SILENT_AFTER_MS;
}

function trailerLabel(trailer: TelemetryTrailerListItem): string {
  return trailer.name || trailer.licensePlate;
}

/**
 * The fleet on a map, with the same fleet as a table beside it.
 *
 * Not a `ListPage`: that layout's contract is a table filling the remaining height, and this page is a
 * table and a map side by side. It is the detail skeleton instead — heading, tab strip, cards.
 *
 * The table is the map's accessible equivalent, so the two carry the same facts and share one highlight:
 * every marker's trailer is a row, and the selected row and the highlighted marker are one piece of state.
 */
export const TrailersMapPage = () => {
  const { i18n, lang } = useTranslation();
  const [search, setSearch] = useState('');
  const [trackerFilter, setTrackerFilter] = useState<TrackerFilter>('all');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [isLive, setIsLive] = useState(false);

  // Only `tracked` is asked of the server; the other two narrow what came back.
  const { data, error, isLoading, mutate } = useTrailerTrackingList(
    { search: search.trim() || undefined, onlyTracked: trackerFilter === 'tracked' },
    isLive ? LIVE_TRACKING_REFRESH_MS : 0,
  );
  const connections = useTelemetryConnections();

  // The three tiles are fleet numbers — how much of the fleet is instrumented, and how much of that has
  // gone quiet — so they ask the fleet rather than the selection. SWR collapses this onto the call above
  // whenever no filter is set; with a filter active it is one extra read of a snapshot the endpoint has
  // already taken.
  const summary = useTrailerTrackingList({}, 0);
  const fleet = summary.data?.trailers ?? [];

  const allTrailers = data?.trailers ?? [];

  const trailers = useMemo(() => {
    const now = Date.now();
    return allTrailers.filter((trailer) => {
      if (trackerFilter === 'untracked') {
        return trailer.tracker === null;
      }
      if (trackerFilter === 'silent') {
        return isSilent(trailer, now);
      }
      return true;
    });
  }, [allTrailers, trackerFilter]);

  // A trailer that has a tracker but no position stays in the table and simply has no marker.
  const markers = useMemo<TrailerMapMarker[]>(
    () =>
      trailers.flatMap((trailer) =>
        trailer.position
          ? [
              {
                id: trailer.id,
                label: trailerLabel(trailer),
                latitude: trailer.position.latitude,
                longitude: trailer.position.longitude,
                status: trailer.position.status,
              },
            ]
          : [],
      ),
    [trailers],
  );

  const trackedCount = fleet.filter((trailer) => trailer.tracker !== null).length;
  const silentCount = fleet.filter((trailer) => isSilent(trailer, Date.now())).length;

  // A filtered-out fleet is not an un-tracked one: saying "no tracker linked yet" while the filter is what
  // emptied the map would deny the very link the page exists to show.
  const fleetHasPosition = fleet.some((trailer) => trailer.tracker !== null && trailer.position !== null);

  const highlightedId = hoveredId ?? selectedId;

  const trackerFilterItems = [
    { key: 'all', name: i18n('liveTracking.trailersMap.trackerAll') },
    { key: 'tracked', name: i18n('liveTracking.trailersMap.trackerTracked') },
    { key: 'untracked', name: i18n('liveTracking.trailersMap.trackerUntracked') },
    { key: 'silent', name: i18n('liveTracking.trailersMap.trackerSilent') },
  ];

  const tableHeaders: ITableHeader[] = [
    { id: 'trailer', name: i18n('liveTracking.trailersMap.tableTrailer') },
    { id: 'licensePlate', name: i18n('liveTracking.trailersMap.tablePlate') },
    { id: 'tracker', name: i18n('liveTracking.trailersMap.tableTracker') },
    { id: 'status', name: i18n('liveTracking.trailersMap.tableStatus') },
    { id: 'lastSignal', name: i18n('liveTracking.trailersMap.tableLastSignal') },
    { id: 'place', name: i18n('liveTracking.trailersMap.tablePlace') },
  ];

  const errorMessage = connections.data?.find((connection) => connection.status === 'Error') ?? null;
  const hasNoConnections = connections.data !== undefined && connections.data.length === 0;

  const activeFilterCount = [search.trim(), trackerFilter !== 'all'].filter(Boolean).length;

  const mapEmptyState =
    fleetHasPosition && activeFilterCount > 0 ? (
      <EmptyState variant="panel" icon={<TruckTrailerIcon />} title={i18n('liveTracking.trailersMap.noneInFilter')} />
    ) : (
      <EmptyState
        variant="panel"
        icon={<TruckTrailerIcon />}
        title={i18n('liveTracking.trailersMap.noneTrackedTitle')}
        description={i18n('liveTracking.trailersMap.noneTrackedDescription')}
      />
    );

  return (
    <>
      <PageHeader title="Trailers" />

      <TrailersHeading />

      <div className="flex flex-col gap-4 px-7 py-5">
        {/* Three numbers, not four: the fleet's KPIs are on the Overzicht tab next door. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatCard variant="compact" label={i18n('liveTracking.trailersMap.trackerTracked')} value={trackedCount} />
          <StatCard
            variant="compact"
            label={i18n('liveTracking.trailersMap.trackerUntracked')}
            value={fleet.length - trackedCount}
          />
          <StatCard variant="compact" label={i18n('liveTracking.trailersMap.trackerSilent')} value={silentCount} />
        </div>

        <FilterPanel
          activeCount={activeFilterCount}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {isLive && <span className="text-sm text-fg-muted">{i18n('liveTracking.liveActive')}</span>}
              <Button
                size={8}
                color="ghost"
                iconLeft={<GaugeIcon className="button-icon" />}
                onTrigger={() => mutate()}
              >
                {i18n('liveTracking.refresh')}
              </Button>
              <Button
                size={8}
                color="secondary"
                iconLeft={<BroadcastIcon className="button-icon" />}
                onTrigger={() => setIsLive((previous) => !previous)}
              >
                {i18n(isLive ? 'liveTracking.stopLive' : 'liveTracking.startLive')}
              </Button>
              <Button
                size={8}
                color="secondary"
                iconLeft={<XIcon className="button-icon" />}
                onTrigger={() => {
                  setSearch('');
                  setTrackerFilter('all');
                }}
              >
                {i18n('liveTracking.trailersMap.clearFilters')}
              </Button>
            </div>
          }
        >
          <FilterField label={i18n('liveTracking.trailersMap.searchLabel')}>
            <Input
              type="text"
              value={search}
              onChange={setSearch}
              placeholder={i18n('liveTracking.trailersMap.searchPlaceholder')}
            />
          </FilterField>

          <FilterField label={i18n('liveTracking.trailersMap.trackerLabel')}>
            <SimpleSelect
              selectedItem={trackerFilterItems.find((item) => item.key === trackerFilter) ?? null}
              items={trackerFilterItems}
              onSelect={(item) => setTrackerFilter(isTrackerFilter(item?.key) ? item.key : 'all')}
              isOptional={true}
            />
          </FilterField>
        </FilterPanel>

        {/* A broken credential belongs where the trailers are, not only on the settings page. */}
        {errorMessage && (
          <Alert type="error" title={i18n('liveTracking.trailersMap.connectionErrorTitle')}>
            {errorMessage.label}
            {errorMessage.lastError ? `: ${errorMessage.lastError}` : ''}
          </Alert>
        )}

        {hasNoConnections ? (
          <EmptyState
            variant="panel"
            icon={<TruckTrailerIcon />}
            title={i18n('liveTracking.trailersMap.noConnectionsTitle')}
            description={i18n('liveTracking.trailersMap.noConnectionsDescription')}
            action={
              <LinkButton to="/internal/integrations/tracking" color="secondary">
                {i18n('liveTracking.trailersMap.noConnectionsAction')}
              </LinkButton>
            }
          />
        ) : (
          <div className="grid min-h-[32rem] grid-cols-1 gap-4 xl:grid-cols-5">
            {/* The table takes the wider share: six columns against one picture. */}
            <Card title={i18n('liveTracking.trailersMap.tableCardTitle')} flush className="xl:col-span-3">
              <Table
                className="h-full"
                idKey="id"
                headers={tableHeaders}
                data={trailers}
                error={error}
                onRetry={() => mutate()}
                isLoading={isLoading}
                hoveredId={highlightedId}
                onHover={(id) => setHoveredId(typeof id === 'number' ? id : null)}
                rowHref={(trailer) => `/internal/trailers/${trailer.id}`}
                onRowClick={(trailer) => setSelectedId(trailer.id)}
                mapData={(trailer) => {
                  const pill = statusPill(trailer.position?.status);
                  const place = trailer.place
                    ? [trailer.place.city, trailer.place.country].filter(Boolean).join(', ')
                    : '';

                  return [
                    <Link to={`/internal/trailers/${trailer.id}`} className="link-text">
                      {trailerLabel(trailer)}
                    </Link>,
                    trailer.licensePlate,
                    trailer.tracker ? (
                      `${trailer.tracker.provider} · ${trailer.tracker.connectionLabel}`
                    ) : (
                      <span className="text-fg-muted">{i18n('liveTracking.trailersMap.noTracker')}</span>
                    ),
                    pill ? (
                      <Pill color={pill.color} size="sm">
                        {i18n(pill.labelKey)}
                      </Pill>
                    ) : (
                      <span className="text-fg-muted">—</span>
                    ),
                    trailer.tracker ? (
                      <Pill
                        color="muted"
                        size="sm"
                        title={new Date(trailer.tracker.lastSeenAt).toLocaleString(lang, {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      >
                        {formatTimeSince(trailer.tracker.lastSeenAt, lang)}
                      </Pill>
                    ) : (
                      <span className="text-fg-muted">—</span>
                    ),
                    place || <span className="text-fg-muted">—</span>,
                  ];
                }}
              />
            </Card>

            <Card title={i18n('liveTracking.trailersMap.mapCardTitle')} className="xl:col-span-2">
              {error && !data ? (
                <ErrorState error={error} variant="section" onRetry={() => mutate()} />
              ) : isLoading && !data ? (
                <DelayedSpinner className="flex items-center justify-center py-24" size={8} />
              ) : markers.length === 0 ? (
                mapEmptyState
              ) : (
                <TrailerMap
                  markers={markers}
                  highlightedId={highlightedId}
                  onMarkerClick={setSelectedId}
                  className="h-[26rem] w-full overflow-hidden rounded-lg xl:h-[30rem]"
                />
              )}
            </Card>
          </div>
        )}
      </div>
    </>
  );
};
